import React, { useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  Users,
  ArrowRight,
  Star,
  FolderOpen,
  Tag,
  BookOpen,
} from "lucide-react";
import { Navigation } from "./Navigation";
import { useCourseStore } from "../store/courseStore";
import LoadingSpinner from "../../packages/ui/src/components/LoadingSpinner";
import SearchBox from "./SearchBox";
import BlogTimeline from "./BlogTimeline";
import SEO from "./SEO";
import TrainingFAQ from "./TrainingFAQ";
import Footer from "./Footer";
import OptimizedImage from "./OptimizedImage";

interface CourseCardProps {
  course: any;
  featured?: boolean;
  selectedTag: string | null;
  onTagClick: (tag: string) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  featured = false,
  selectedTag,
  onTagClick,
}) => (
  <div
    id={course.slug}
    className={`bg-white rounded-lg border ${featured ? "border-[#F98B3D]" : "border-gray-200"} shadow-sm hover:shadow-md transition-all duration-200 flex flex-col h-full`}
  >
    {course.image_url && (
      <OptimizedImage
        src={course.image_url}
        alt={course.title}
        width={400}
        height={192}
        loading="lazy"
        className="w-full h-48 object-cover rounded-t-lg"
      />
    )}
    <div className="p-6 flex flex-col flex-grow">
      {featured && (
        <div className="flex items-center text-[#F98B3D] mb-2">
          <Star className="h-5 w-5 mr-1" />
          <span className="text-sm font-medium">Featured Course</span>
        </div>
      )}
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {course.title}
      </h3>
      <p className="text-gray-600 mb-4 line-clamp-2">{course.description}</p>
      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
        <div className="flex items-center">
          <Clock className="h-4 w-4 mr-1" />
          <span>{course.duration}</span>
        </div>
        <div className="flex items-center">
          <Users className="h-4 w-4 mr-1" />
          <span>Max {course.max_students} students</span>
        </div>
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-1" />
          <span>{course.level}</span>
        </div>
      </div>
      {course.tags && course.tags.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 mb-4">
          <Tag className="h-4 w-4 text-gray-400" />
          {course.tags.map(
            (tagMap: any) =>
              tagMap.course_tags && (
                <button
                  key={tagMap.course_tags.id}
                  onClick={(e) => {
                    e.preventDefault();
                    onTagClick(tagMap.course_tags.name);
                  }}
                  className={`text-xs font-medium ${
                    selectedTag === tagMap.course_tags.name
                      ? "text-[#F98B3D]"
                      : "text-gray-500 hover:text-[#F98B3D]"
                  } transition-colors duration-200`}
                >
                  #{tagMap.course_tags.name}
                </button>
              ),
          )}
        </div>
      )}
      <div className="mt-auto flex justify-end">
        <Link
          to={`/course/${course.slug}`}
          className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium ${
            featured
              ? "bg-[#F98B3D] text-white hover:bg-[#e07a2c]"
              : "bg-gray-100 text-gray-900 hover:bg-gray-200"
          } transition-colors duration-200`}
        >
          Learn More
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default function CourseCatalog() {
  const {
    courses,
    featuredCourses,
    categories,
    selectedTag,
    loading,
    error,
    fetchCourses,
    fetchFeaturedCourses,
    fetchCategories,
    setSelectedTag,
  } = useCourseStore();

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCalOpen, setIsCalOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    // Optimize initial load by combining data fetching
    const loadInitialData = async () => {
      try {
        // Load all data in parallel instead of sequentially
        await Promise.all([
          fetchFeaturedCourses(),
          fetchCategories(),
          fetchCourses(),
        ]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadInitialData();
  }, [fetchFeaturedCourses, fetchCategories, fetchCourses]);

  useEffect(() => {
    // Only fetch courses if tag changes (not on initial load)
    if (!isInitialLoad) {
      const tagFromUrl = searchParams.get("tag");
      if (tagFromUrl && tagFromUrl !== selectedTag) {
        setSelectedTag(tagFromUrl);
      }
    }
  }, [searchParams, selectedTag, setSelectedTag, isInitialLoad]);

  const handleTagClick = (tag: string) => {
    setSearchParams({ tag });
    setSelectedTag(tag);
    setSearchQuery(""); // Clear search when filtering by tag
  };

  const clearTagFilter = () => {
    setSearchParams({});
    setSelectedTag(null);
    setSearchQuery(""); // Clear search when clearing filters
  };

  // Filter courses based on search query and selected tag
  const filteredCourses = courses.filter((course) => {
    const matchesSearch = searchQuery
      ? course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    const matchesTag = selectedTag
      ? course.tags?.some(
          (tagMap: any) => tagMap.course_tags?.name === selectedTag,
        )
      : true;

    return matchesSearch && matchesTag;
  });

  // Enhanced schema for courses page with local business and FAQ data
  const coursePageSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: courses.map((course, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Course",
        name: course.title,
        description: course.description,
        provider: {
          "@type": "Organization",
          name: "One80Labs",
          sameAs: "https://one80labs.com",
        },
        url: `https://one80labs.com/course/${course.slug}`,
        offers: {
          "@type": "Offer",
          price: course.price,
          priceCurrency: "USD",
        },
      },
    })),
  };

  // Local business schema for better local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs - AI Training in Omaha",
    image: "https://one80labs.com/logo.png",
    description:
      "The Midwest's Premier AI Agency providing AI training in Omaha, Nebraska and surrounding areas. Learn ChatGPT, Gemini, and Microsoft 365 Copilot.",
    "@id": "https://one80labs.com",
    url: "https://one80labs.com/courses",
    telephone: "+1-402-915-3613",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 41.2651,
        longitude: -95.9397,
      },
      geoRadius: "500",
      description:
        "Serving Omaha, Lincoln, Council Bluffs, Bellevue, Papillion, La Vista, and the greater Midwest region including Nebraska, Iowa, Kansas, Missouri, and South Dakota",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Training Courses",
      itemListElement: courses.map((course) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Course",
          name: course.title,
          description: course.description,
        },
      })),
    },
  };

  // FAQ schema for the training page
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What AI training courses does One80Labs offer in Omaha?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "One80Labs offers comprehensive AI training courses in Omaha including ChatGPT workshops, Google Gemini training, Microsoft 365 Copilot courses, and custom AI implementation training. Our courses are designed for professionals at all levels and can be delivered in-person or virtually throughout the Midwest region.",
        },
      },
      {
        "@type": "Question",
        name: "How much do One80Labs AI training courses cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "One80Labs offers both free and paid AI training courses. Our free 3-hour ChatGPT workshop is available to Omaha and Midwest businesses, while comprehensive courses range from $497 to $997 depending on the course length and content. We also offer custom training packages for organizations.",
        },
      },
      {
        "@type": "Question",
        name: "Where are One80Labs AI training courses held?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "One80Labs AI training courses are held in Omaha, Nebraska at our training facility, as well as virtually for participants throughout the Midwest region. We also offer on-site training for organizations in Nebraska, Iowa, Kansas, Missouri, and South Dakota.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need technical skills to attend One80Labs AI training?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No technical background is required for most One80Labs AI training courses. Our courses are designed for beginners to intermediate users and focus on practical applications rather than technical implementation. We provide hands-on exercises and real business scenarios.",
        },
      },
      {
        "@type": "Question",
        name: "How do I register for One80Labs AI training courses?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "To register for One80Labs AI training courses, contact us at hello@one80labs.com or call +1-402-915-3613. You can also visit our website to browse available courses and schedule a consultation to discuss your specific training needs.",
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title="AI Training Courses in Omaha | One80Labs"
        description="Comprehensive AI training courses in Omaha, Nebraska. Learn ChatGPT, Google Gemini, and Microsoft 365 Copilot from the Midwest's premier AI agency. Free and paid courses available."
        image="https://one80labs.com/logo.png"
        jsonLd={[coursePageSchema, localBusinessSchema, faqSchema]}
        keywords={[
          "AI training Omaha",
          "ChatGPT courses Nebraska",
          "Google Gemini training",
          "Microsoft 365 Copilot courses",
          "AI workshops Omaha",
          "Midwest AI training",
          "Omaha technology courses",
          "AI implementation training",
        ]}
      />
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                AI Training Courses
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Master AI tools like ChatGPT, Google Gemini, and Microsoft 365
                Copilot with hands-on training from the Midwest's premier AI
                agency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setIsCalOpen(true)}
                  className="bg-[#F98B3D] text-white px-8 py-3 rounded-md font-medium hover:bg-[#e07a2c] transition-colors duration-200"
                >
                  Schedule Free Consultation
                </button>
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className="bg-gray-100 text-gray-900 px-8 py-3 rounded-md font-medium hover:bg-gray-200 transition-colors duration-200"
                >
                  {showSearch ? "Hide Search" : "Search Courses"}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Search Bar */}
        {showSearch && (
          <section className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search courses by title or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F98B3D] focus:border-[#F98B3D] outline-none transition-colors duration-200"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {(searchQuery || selectedTag) && (
                  <div className="mt-4 flex flex-wrap gap-2 items-center">
                    {searchQuery && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#F98B3D] text-white">
                        Search: "{searchQuery}"
                        <button
                          onClick={() => setSearchQuery("")}
                          className="ml-2 hover:text-gray-200"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedTag && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-200 text-gray-700">
                        Category: {selectedTag}
                        <button
                          onClick={() => clearTagFilter()}
                          className="ml-2 hover:text-gray-500"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {(searchQuery || selectedTag) && (
                      <button
                        onClick={() => {
                          setSearchQuery("");
                          clearTagFilter();
                        }}
                        className="text-sm text-[#F98B3D] hover:text-[#e07a2c] font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Featured Courses */}
        {featuredCourses.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Featured Courses
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    featured={true}
                    selectedTag={selectedTag}
                    onTagClick={handleTagClick}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Courses */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">
                All Courses
              </h2>
              {selectedTag && (
                <button
                  onClick={clearTagFilter}
                  className="text-[#F98B3D] hover:text-[#e07a2c] font-medium"
                >
                  Clear Filter
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={() => fetchCourses()}
                  className="mt-4 bg-[#F98B3D] text-white px-6 py-2 rounded-md hover:bg-[#e07a2c]"
                >
                  Try Again
                </button>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No courses found
                </h3>
                <p className="text-gray-600">
                  {searchQuery
                    ? `No courses found matching "${searchQuery}"`
                    : selectedTag
                      ? `No courses found for "${selectedTag}"`
                      : "No courses are currently available."}
                </p>
                {(searchQuery || selectedTag) && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      clearTagFilter();
                    }}
                    className="mt-4 bg-[#F98B3D] text-white px-6 py-2 rounded-md hover:bg-[#e07a2c]"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    selectedTag={selectedTag}
                    onTagClick={handleTagClick}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Custom AI Training Plan Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gray-200 border border-gray-300 rounded-full flex items-center justify-center">
                    <BookOpen className="h-8 w-8 text-gray-700" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Get a Custom AI Training Plan
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Whether you're in Omaha or anywhere in the Midwest, our AI
                    training helps you and your team work smarter, faster, and
                    better.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={() => setIsCalOpen(true)}
                    className="bg-[#F98B3D] text-white px-6 py-3 rounded-md font-medium hover:bg-[#e07a2c] transition-colors duration-200 flex items-center gap-2"
                  >
                    Schedule a Conversation Today
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <TrainingFAQ />
          </div>
        </section>

        {/* Blog Timeline Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <BlogTimeline />
          </div>
        </section>

        <Footer />
      </div>

      {/* Cal Widget for scheduling */}
      {isCalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={() => setIsCalOpen(false)}
              className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <iframe
              src="https://cal.com/one80/30min?embed=true"
              style={{
                width: "100%",
                height: "100%",
                overflow: "scroll",
                borderRadius: "8px",
              }}
              frameBorder="0"
            ></iframe>
          </div>
        </div>
      )}
    </>
  );
}
